import React, { useState, useEffect } from 'react';
import { Check, X, Sparkles, Package, ArrowRight, TrendingUp, AlertCircle, Users, Zap, Shield, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { captureEvent } from '../utils/posthog';
import { PostHogEvents } from '../utils/posthogEvents';

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedModules, setSelectedModules] = useState(['metier']); // Métier obligatoire
  const [showPackSuggestion, setShowPackSuggestion] = useState(false);
  const [suggestedPack, setSuggestedPack] = useState(null);
  const [userCount, setUserCount] = useState(10);
  const [showContactForm, setShowContactForm] = useState(false);
  const [customUserCount, setCustomUserCount] = useState('');
  const [isAnnual, setIsAnnual] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cursorPercentage, setCursorPercentage] = useState(0); // Position du curseur de 0 à 1 (0% à 100%)

  // Track page view
  useEffect(() => {
    captureEvent(PostHogEvents.PRICING_PAGE_VIEWED, {
      user_id: user?.id,
      subscription_plan: user?.subscriptionPlan || 'free',
      is_authenticated: !!user
    });
  }, [user]);

  // Gestion du slider circulaire
  const handleCircularSliderMove = (e, centerX, centerY) => {
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    const x = clientX - centerX;
    const y = clientY - centerY;

    // Calculer l'angle en degrés (0° = haut, sens horaire)
    // atan2(y, x) donne l'angle avec x comme référence
    // On soustrait 90° pour avoir le haut (12h) comme 0°
    let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;

    // Normaliser l'angle de 0 à 1 (0% à 100%)
    const percentage = angle / 360;

    // TOUJOURS stocker le pourcentage réel pour que le curseur suive exactement
    setCursorPercentage(percentage);

    let value;
    if (percentage <= 0.75) {
      // De 0% à 75% = 1 à 50 utilisateurs (progression linéaire)
      value = Math.round(1 + (percentage / 0.75) * 49);
    } else {
      // De 75% à 100% = zone "devis sur mesure" - valeur 51
      value = 51;
    }

    const clampedValue = Math.max(1, Math.min(51, value));
    setUserCount(clampedValue);

    // Si on dépasse 50, afficher le formulaire de contact
    if (clampedValue > 50) {
      setShowContactForm(true);
    } else {
      setShowContactForm(false);
      setCustomUserCount('');
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault(); // Empêcher la sélection de texte
    setIsDragging(true);
    const circle = e.currentTarget.closest('.circular-slider-container');
    const rect = circle.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    handleCircularSliderMove(e, centerX, centerY);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault(); // Empêcher la sélection de texte
    const circle = document.querySelector('.circular-slider-container');
    if (!circle) return;
    const rect = circle.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    handleCircularSliderMove(e, centerX, centerY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      // Ajouter les events avec passive: false pour permettre preventDefault
      window.addEventListener('mousemove', handleMouseMove, { passive: false });
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      // Empêcher la sélection de texte pendant le drag
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging]);

  // Gestion du nombre d'utilisateurs personnalisé
  const handleCustomUserCountChange = (e) => {
    const value = e.target.value;
    // Autoriser uniquement les nombres
    if (value === '' || /^\d+$/.test(value)) {
      setCustomUserCount(value);
      const numValue = parseInt(value) || 0;
      if (numValue > 50) {
        setUserCount(numValue);
      }
    }
  };

  // Calcul du coefficient de prix basé sur le nombre d'utilisateurs
  const getPriceMultiplier = () => {
    if (userCount <= 10) return 1;
    if (userCount <= 30) return 1.5;
    if (userCount <= 50) return 2;
    // Au-delà de 50, prix personnalisé
    return 2.5; // Coefficient par défaut pour l'affichage
  };

  // Calcul du prix annuel : (prix mensuel × 12) - 15%
  const getAnnualPrice = (monthlyPrice) => {
    if (!isAnnual) return monthlyPrice;
    return (monthlyPrice * 12) * 0.85;
  };

  // Calcul des économies réalisées avec le paiement annuel
  const getSavings = (monthlyPrice) => {
    const yearlyWithoutDiscount = monthlyPrice * 12;
    const yearlyWithDiscount = yearlyWithoutDiscount * 0.85;
    return Math.round(yearlyWithoutDiscount - yearlyWithDiscount);
  };

  // Formatage du prix avec la périodicité
  const formatPrice = (price) => {
    if (userCount > 50) return 'Sur devis';
    const finalPrice = getAnnualPrice(price);
    return `${Math.round(finalPrice)}€${isAnnual ? '/an' : '/mois'}`;
  };

  // Définition des modules avec prix de base
  const modules = {
    metier: {
      id: 'metier',
      name: 'Métier',
      description: 'Plateforme de base',
      basePrice: 200, // Prix pour 10 utilisateurs
      required: true
    },
    chaine: {
      id: 'chaine',
      name: 'Chaîne',
      description: 'Import référentiels',
      basePrice: 50
    },
    navette: {
      id: 'navette',
      name: 'Navette',
      description: 'Fusion/matching de compétences',
      basePrice: 100
    },
    trame: {
      id: 'trame',
      name: 'Trame',
      description: 'Datavisualisation',
      basePrice: 100
    },
    aiguille: {
      id: 'aiguille',
      name: 'Aiguille',
      description: 'Recommandations / matching fin',
      basePrice: 100
    },
    tapis: {
      id: 'tapis',
      name: 'Tapis',
      description: 'Plan de montée en compétences',
      basePrice: 80
    },
    echeveau: {
      id: 'echeveau',
      name: 'Écheveau',
      description: 'VAE / mobilité',
      basePrice: 50
    },
    fusette: {
      id: 'fusette',
      name: 'Fusette',
      description: 'Modules LMS / contenu généré',
      basePrice: 100
    },
    ourdissage: {
      id: 'ourdissage',
      name: 'Ourdissage',
      description: 'Emploi du temps / classes mixtes',
      basePrice: 50
    }
  };

  // Calcul du prix d'un module en fonction du nombre d'utilisateurs
  const getModulePrice = (module) => {
    const multiplier = getPriceMultiplier();
    return Math.round(module.basePrice * multiplier);
  };

  // Définition des packs
  const packs = {
    basique: {
      id: 'basique',
      name: 'Pack Basique',
      modules: ['metier', 'chaine', 'trame'],
      basePrice: 125,
      savings: 200,
      gradient: 'from-blue-50 to-cyan-50',
      iconColor: 'text-blue-600'
    },
    standard: {
      id: 'standard',
      name: 'Pack Standard',
      modules: ['metier', 'chaine', 'trame', 'navette', 'aiguille', 'ourdissage'],
      basePrice: 250,
      savings: 600,
      gradient: 'from-purple-50 to-pink-50',
      iconColor: 'text-purple-600',
      popular: true
    },
    confort: {
      id: 'confort',
      name: 'Pack Confort',
      modules: ['metier', 'chaine', 'trame', 'navette', 'aiguille', 'ourdissage', 'tapis', 'echeveau'],
      basePrice: 350,
      savings: 1000,
      gradient: 'from-green-50 to-emerald-50',
      iconColor: 'text-green-600'
    },
    premium: {
      id: 'premium',
      name: 'Pack Premium',
      modules: Object.keys(modules),
      basePrice: 400,
      savings: 1500,
      gradient: 'from-yellow-50 to-orange-50',
      iconColor: 'text-yellow-600'
    }
  };

  // Calcul du prix d'un pack
  const getPackPrice = (pack) => {
    const multiplier = getPriceMultiplier();
    return Math.round(pack.basePrice * multiplier);
  };

  // Calcul du prix total des modules sélectionnés
  const calculateTotal = () => {
    const currentPackId = getSelectedPackId();

    if (currentPackId) {
      // Si un pack est sélectionné, on prend le prix du pack
      const pack = packs[currentPackId];
      const packPrice = getPackPrice(pack);

      // On ajoute le prix des modules supplémentaires (hors pack)
      const extraModules = selectedModules.filter(moduleId => !pack.modules.includes(moduleId));
      const extraPrice = extraModules.reduce((sum, moduleId) => sum + getModulePrice(modules[moduleId]), 0);

      return packPrice + extraPrice;
    }

    // Sinon, on additionne tous les modules sélectionnés
    const total = selectedModules.reduce((sum, moduleId) => sum + getModulePrice(modules[moduleId]), 0);
    return total;
  };

  // Vérification si la sélection correspond à un pack
  const checkForPackMatch = (selected) => {
    const selectedSorted = [...selected].sort();

    // Vérifier si la sélection correspond exactement à un pack
    for (const [packId, pack] of Object.entries(packs)) {
      const packModules = [...pack.modules].sort();
      if (JSON.stringify(packModules) === JSON.stringify(selectedSorted)) {
        return null; // Pas de suggestion si c'est déjà un pack complet
      }
    }

    // Si l'utilisateur a plus de modules qu'un pack, ne pas suggérer ce pack
    const currentPackId = getSelectedPackId();

    // Vérifier chaque pack pour voir s'il peut être suggéré
    const packOrder = ['basique', 'standard', 'confort', 'premium'];

    for (const packId of packOrder) {
      const pack = packs[packId];
      const packModules = pack.modules;
      const matchCount = packModules.filter(m => selectedSorted.includes(m)).length;

      // Règles spécifiques par pack
      let shouldSuggest = false;

      if (packId === 'basique') {
        // Pour le pack basique : au moins Chaîne OU Trame (en plus de Métier obligatoire)
        const hasChaine = selectedSorted.includes('chaine');
        const hasTrame = selectedSorted.includes('trame');
        shouldSuggest = (hasChaine || hasTrame) && selectedSorted.length <= packModules.length;
      } else if (packId === 'standard') {
        // Pour le pack standard : au moins 4 des options du pack
        shouldSuggest = matchCount >= 4 && selectedSorted.length <= packModules.length + 1;
      } else if (packId === 'confort') {
        // Pour le pack confort : au moins 6 des options du pack
        shouldSuggest = matchCount >= 6 && selectedSorted.length <= packModules.length + 1;
      } else if (packId === 'premium') {
        // Pour le pack premium : au moins 8 des options du pack
        shouldSuggest = matchCount >= 8 && selectedSorted.length <= packModules.length + 1;
      }

      if (shouldSuggest) {
        return pack;
      }
    }

    return null;
  };

  // Gestion de la sélection des modules
  const toggleModule = (moduleId) => {
    if (moduleId === 'metier') return; // Métier est obligatoire

    const newSelection = selectedModules.includes(moduleId)
      ? selectedModules.filter(id => id !== moduleId)
      : [...selectedModules, moduleId];

    setSelectedModules(newSelection);

    // Vérifier si on peut suggérer un pack
    const matchedPack = checkForPackMatch(newSelection);
    if (matchedPack) {
      setSuggestedPack(matchedPack);
      setShowPackSuggestion(true);
    } else {
      setShowPackSuggestion(false);
      setSuggestedPack(null);
    }
  };

  // Sélection d'un pack complet
  const selectPack = (packId) => {
    setSelectedModules(packs[packId].modules);
    setShowPackSuggestion(false);
    setSuggestedPack(null);
  };

  // Vérifier si la sélection correspond exactement à un pack
  const getSelectedPackId = () => {
    for (const [packId, pack] of Object.entries(packs)) {
      const packModules = [...pack.modules].sort();
      const selectedSorted = [...selectedModules].sort();
      if (JSON.stringify(packModules) === JSON.stringify(selectedSorted)) {
        return packId;
      }
    }
    return null;
  };

  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-semibold mb-6 animate-bounce-in">
            <Zap className="w-4 h-4" />
            <span>Offre de lancement - 15% de réduction annuelle</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 animate-fade-in-up">
            Construisez votre offre sur mesure
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 animate-fade-in-up-delay">
            Choisissez uniquement les modules dont vous avez besoin, ou optez pour un pack avantageux
          </p>

          {/* Badges de confiance */}
          <div className="flex flex-wrap justify-center gap-6 mt-8 animate-fade-in-up-delay-2">
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium">Sécurisé et conforme RGPD</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Rocket className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">Déploiement en 48h</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium">Support dédié inclus</span>
            </div>
          </div>
        </div>

        {/* Sélecteur du nombre d'utilisateurs */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="w-8 h-8 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Nombre d'utilisateurs
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Slider circulaire */}
            <div className="lg:col-span-2">
              {/* Affichage central du nombre d'utilisateurs avec slider circulaire */}
              <div className="flex flex-col items-center mb-12">
                <div
                  className="circular-slider-container relative w-80 h-80 flex items-center justify-center select-none"
                  style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
                >
                  {/* Cercle de fond avec piste */}
                  <svg className="absolute w-full h-full transform -rotate-90" style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' }}>
                    {/* Piste de fond */}
                    <circle
                      cx="160"
                      cy="160"
                      r="140"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                    />
                    {/* Piste de progression avec gradient */}
                    <circle
                      cx="160"
                      cy="160"
                      r="140"
                      fill="none"
                      stroke="url(#gradient)"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${cursorPercentage <= 0.75 ? cursorPercentage * 879.65 : 0.75 * 879.65} 879.65`}
                      style={{ transition: isDragging ? 'none' : 'stroke-dasharray 0.3s ease' }}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="30%" stopColor="#8b5cf6" />
                        <stop offset="60%" stopColor="#10b981" />
                        <stop offset="100%" stopColor="#f59e0b" />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* Labels autour du cercle */}
                  {[
                    { value: 10, percentage: 0.15, color: 'text-blue-600' },
                    { value: 20, percentage: 0.30, color: 'text-blue-600' },
                    { value: 30, percentage: 0.45, color: 'text-purple-600' },
                    { value: 40, percentage: 0.60, color: 'text-purple-600' },
                    { value: 50, percentage: 0.75, color: 'text-green-600' }
                  ].map(({ value, percentage, color }, idx) => {
                    const angle = percentage * 360 - 90;
                    const radian = angle * (Math.PI / 180);
                    const x = 160 + 155 * Math.cos(radian);
                    const y = 160 + 155 * Math.sin(radian);
                    const isActive = userCount === value;
                    return (
                      <div
                        key={idx}
                        className={`absolute text-sm font-semibold select-none pointer-events-none ${isActive ? `${color} font-bold scale-125` : 'text-gray-500'} transition-all`}
                        style={{
                          left: `${x}px`,
                          top: `${y}px`,
                          transform: 'translate(-50%, -50%)',
                          userSelect: 'none'
                        }}
                      >
                        {value}
                      </div>
                    );
                  })}

                  {/* Curseur circulaire draggable */}
                  {(() => {
                    // Calcul de l'angle basé sur la position du curseur (0-100%)
                    const angle = cursorPercentage * 360 - 90;
                    const radian = angle * (Math.PI / 180);
                    const x = 160 + 140 * Math.cos(radian);
                    const y = 160 + 140 * Math.sin(radian);

                    return (
                      <div
                        className={`absolute w-8 h-8 bg-white rounded-full shadow-2xl border-4 ${
                          cursorPercentage > 0.75 ? 'border-orange-600' : 'border-primary-600'
                        } cursor-grab active:cursor-grabbing hover:scale-125 z-10 select-none`}
                        style={{
                          left: `${x}px`,
                          top: `${y}px`,
                          transform: 'translate(-50%, -50%)',
                          transition: isDragging ? 'none' : 'transform 0.1s ease-out, border-color 0.3s ease',
                          userSelect: 'none'
                        }}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleMouseDown}
                      />
                    );
                  })()}

                  {/* Cercle central avec le nombre */}
                  <div className="absolute w-48 h-48 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center shadow-2xl pointer-events-none select-none">
                    <div className="text-center select-none">
                      <div className="text-6xl font-bold text-white select-none">
                        {userCount > 50 ? '50+' : userCount}
                      </div>
                      <div className="text-sm text-white/90 mt-2 select-none">
                        utilisateur{userCount > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Badge devis personnalisé */}
                  {userCount > 50 && (
                    <div className="absolute top-8 right-8 bg-orange-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg animate-bounce-in z-20">
                      Devis sur mesure
                    </div>
                  )}
                </div>
              </div>

              {/* Toggle Annuel/Mensuel */}
              <div className="flex items-center justify-center gap-4">
                <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                  Mensuel
                </span>
                <button
                  onClick={() => setIsAnnual(!isAnnual)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isAnnual ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isAnnual ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-500'}`}>
                  Annuel <span className="text-green-600 font-semibold">(-15%)</span>
                </span>
              </div>
            </div>

            {/* Zone de contact pour plus de 50 utilisateurs */}
            {showContactForm && (
              <div className="lg:col-span-1 bg-orange-50 border border-orange-200 rounded-xl p-6 animate-fade-in">
                <div className="flex items-start gap-3 mb-4">
                  <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-orange-900 mb-2">
                      Configuration personnalisée requise
                    </h3>
                    <p className="text-orange-800 text-sm">
                      Pour plus de 50 utilisateurs, nous créons une offre sur mesure adaptée à vos besoins spécifiques.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-900 mb-2">
                      Nombre exact d'utilisateurs
                    </label>
                    <input
                      type="text"
                      value={customUserCount}
                      onChange={handleCustomUserCountChange}
                      placeholder="Ex: 75, 120, 250..."
                      className="w-full px-4 py-3 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                    />
                  </div>

                  <button
                    onClick={() => window.open('mailto:contact@orbloom.fr?subject=Devis pour ' + (customUserCount || 'plus de 50') + ' utilisateurs&body=Bonjour, je souhaiterais obtenir un devis personnalisé pour ' + (customUserCount || 'plus de 50') + ' utilisateurs.')}
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-orange-700 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    Nous contacter pour un devis
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Packs pré-configurés */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            💡 Nos packs recommandés
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.values(packs).map((pack) => {
              const packPrice = getPackPrice(pack);
              const isSelected = getSelectedPackId() === pack.id;
              return (
                <div
                  key={pack.id}
                  className={`relative bg-gradient-to-br ${pack.gradient} rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border-2 ${
                    isSelected
                      ? 'ring-2 ring-primary-600 scale-105 border-primary-400'
                      : 'border-transparent hover:border-primary-400'
                  }`}
                  onClick={() => selectPack(pack.id)}
                >
                  {pack.popular && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-primary-600 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-bl-lg">
                      POPULAIRE
                    </div>
                  )}

                  <div className="p-6">
                    <div className={`w-12 h-12 ${pack.iconColor} mb-4 flex items-center justify-center`}>
                      <Package className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pack.name}</h3>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">
                        {formatPrice(packPrice)}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      {pack.modules.map((moduleId) => (
                        <div key={moduleId} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>{modules[moduleId].name}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      className={`w-full ${
                        userCount > 50
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-900 hover:bg-gray-50'
                      } py-2 px-4 rounded-lg font-semibold transition-colors`}
                      disabled={userCount > 50}
                    >
                      {userCount > 50 ? 'Nous contacter' : 'Sélectionner ce pack'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Séparateur */}
        <div className="flex items-center gap-4 mb-12">
          <div className="flex-1 h-px bg-gray-300"></div>
          <span className="text-gray-500 font-semibold">OU</span>
          <div className="flex-1 h-px bg-gray-300"></div>
        </div>

        {/* Construction personnalisée */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Liste des modules */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              🔧 Personnalisez votre offre
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.values(modules).map((module) => {
                const modulePrice = getModulePrice(module);
                return (
                  <div
                    key={module.id}
                    onClick={() => userCount <= 50 && toggleModule(module.id)}
                    className={`bg-white rounded-xl p-6 border-2 transition-all duration-200 ${
                      selectedModules.includes(module.id)
                        ? 'border-primary-500 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${
                      module.required ? 'opacity-75 cursor-not-allowed' : 
                      userCount > 50 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{module.name}</h3>
                        <p className="text-sm text-gray-600">{module.description}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedModules.includes(module.id)
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedModules.includes(module.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">
                      {formatPrice(modulePrice)}
                    </div>
                    {module.required && (
                      <div className="mt-2 text-xs text-primary-600 font-semibold">
                        Module obligatoire
                      </div>
                    )}
                    {userCount > 50 && !module.required && (
                      <div className="mt-2 text-xs text-orange-600 font-semibold">
                        Contactez-nous
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Résumé de la commande */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 bg-white rounded-2xl shadow-xl p-6 border-2 border-primary-100">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Votre sélection</h3>

              <div className="space-y-3 mb-6">
                {(() => {
                  const currentPackId = getSelectedPackId();
                  if (currentPackId) {
                    const pack = packs[currentPackId];
                    const packPrice = getPackPrice(pack);
                    return (
                      <>
                        <div className="flex items-center justify-between text-sm mb-3 pb-3 border-b border-gray-200">
                          <span className="text-gray-900 font-bold">{pack.name}</span>
                          <span className="font-bold text-gray-900">
                            {formatPrice(packPrice)}
                          </span>
                        </div>
                        {selectedModules.map((moduleId) => (
                          <div key={moduleId} className="flex items-center gap-2 text-sm text-gray-600 ml-2">
                            <Check className="w-3 h-3 text-green-600" />
                            <span>{modules[moduleId].name}</span>
                          </div>
                        ))}
                      </>
                    );
                  }
                  return selectedModules.map((moduleId) => (
                    <div key={moduleId} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{modules[moduleId].name}</span>
                      <span className="font-semibold text-gray-900">
                        {formatPrice(getModulePrice(modules[moduleId]))}
                      </span>
                    </div>
                  ));
                })()}
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Total {isAnnual ? 'annuel' : 'mensuel'}</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {userCount <= 50 ? formatPrice(total) : 'Sur devis'}
                  </span>
                </div>
                {isAnnual && userCount <= 50 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
                    <p className="text-sm font-semibold text-green-800">
                      ✓ Vous économisez {getSavings(total)}€ avec le paiement annuel
                    </p>
                  </div>
                )}
                {!isAnnual && userCount <= 50 && (
                  <div
                    onClick={() => setIsAnnual(true)}
                    className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-2 cursor-pointer hover:bg-blue-100 transition-colors"
                  >
                    <p className="text-sm font-semibold text-blue-800">
                      💡 Passez en annuel et économisez {getSavings(total)}€ !
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Pour {userCount} utilisateur{userCount > 1 ? 's' : ''}
                  {userCount <= 50 && ` • ${Math.round(total / userCount)}€/utilisateur/mois`}
                  {userCount > 50 && ' - Devis personnalisé requis'}
                </p>
              </div>

              <button
                onClick={() => {
                  if (userCount > 50) {
                    window.open('mailto:contact@orbloom.fr?subject=Devis pour ' + (customUserCount || userCount) + ' utilisateurs&body=Bonjour, je souhaiterais obtenir un devis personnalisé pour ' + (customUserCount || userCount) + ' utilisateurs. Modules sélectionnés: ' + selectedModules.map(id => modules[id].name).join(', '));
                  } else {
                    navigate('/register');
                  }
                }}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${
                  userCount > 50
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:from-orange-700 hover:to-red-700'
                    : 'bg-gradient-to-r from-primary-600 to-purple-600 text-white hover:from-primary-700 hover:to-purple-700'
                }`}
              >
                {userCount > 50 ? 'Nous contacter pour un devis' : 'Commencer maintenant'}
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                {userCount <= 50
                  ? (isAnnual
                      ? 'Facturation annuelle • Engagement pour 12 mois, non renouvelé automatiquement'
                      : 'Sans engagement • Annulation à tout moment')
                  : 'Réponse sous 24h'}
              </p>
            </div>
          </div>
        </div>

        {/* Suggestion de pack (sidebar fixe à droite) */}
        {showPackSuggestion && suggestedPack && userCount <= 50 && (
          <div className="fixed right-0 top-1/2 -translate-y-1/2 bg-white rounded-l-2xl shadow-2xl p-6 max-w-sm border-l-4 border-primary-500 animate-slide-in-right z-50">
            <button
              onClick={() => setShowPackSuggestion(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900">Économisez plus !</h4>
                <p className="text-sm text-gray-600">
                  Votre sélection est proche du <span className="font-semibold">{suggestedPack.name}</span>
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-green-600" />
                <span className="text-lg font-bold text-green-800">
                  {suggestedPack.savings}€/an économisés
                </span>
              </div>
              <p className="text-sm text-green-700">
                En prenant le {suggestedPack.name} au lieu de modules séparés
              </p>
            </div>

            <button
              onClick={() => selectPack(suggestedPack.id)}
              className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all duration-200"
            >
              Passer au {suggestedPack.name}
            </button>
          </div>
        )}

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-3xl mx-auto mt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Questions fréquentes
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Comment le prix varie-t-il avec le nombre d'utilisateurs ?
              </h3>
              <p className="text-gray-600 text-sm">
                Nos prix sont échelonnés selon quatre paliers : 1, 10, 30 et 50 utilisateurs. 
                Au-delà de 50 utilisateurs, nous créons une offre personnalisée adaptée à vos besoins spécifiques.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Y a-t-il une réduction pour le paiement annuel ?
              </h3>
              <p className="text-gray-600 text-sm">
                Oui, nous offrons une réduction de 15% pour les paiements annuels. Cette réduction est automatiquement appliquée au total de votre sélection.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Puis-je commencer avec un seul utilisateur ?
              </h3>
              <p className="text-gray-600 text-sm">
                Oui, vous pouvez commencer avec un seul utilisateur et étendre progressivement. 
                Le prix est ajusté automatiquement selon le nombre d'utilisateurs.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-4">
            Besoin d'aide pour choisir ?
          </p>
          <a
            href="mailto:contact@orbloom.fr"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold"
          >
            Contactez notre équipe
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce-in {
          0% {
            opacity: 0;
            transform: scale(0.9);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out;
        }

        .animate-fade-in-up-delay {
          animation: fade-in-up 0.5s ease-out 0.1s backwards;
        }

        .animate-fade-in-up-delay-2 {
          animation: fade-in-up 0.5s ease-out 0.2s backwards;
        }

        /* Style personnalisé pour le slider */
        .slider {
          background: linear-gradient(to right,
            #3b82f6 0%,
            #3b82f6 18%,
            #8b5cf6 18%,
            #8b5cf6 29%,
            #10b981 29%,
            #10b981 49%,
            #f59e0b 49%,
            #f59e0b 100%
          );
          transition: all 0.3s ease;
        }

        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }

        .slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          background: #2563eb;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .slider::-webkit-slider-thumb:active {
          transform: scale(1.15);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5);
        }

        .slider::-moz-range-thumb {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: all 0.2s ease;
        }

        .slider::-moz-range-thumb:hover {
          transform: scale(1.1);
          background: #2563eb;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .slider::-webkit-slider-thumb {
            height: 28px;
            width: 28px;
          }
          .slider::-moz-range-thumb {
            height: 28px;
            width: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default Pricing;